import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Eye, Search, Funnel } from 'lucide-react';
import ReusableDataTable from '../../components/table/ReusableDataTable';
import { getOrderValidationList, updateOrderValidationStatus } from '../../store/thunk/adminThunk';
import { encodeToBase64, formatPrice, formatToUSDate } from '../../hooks/commonFunction';
import toast from 'react-hot-toast';
import useIsMobile from '../../hooks/useIsMobile';

const OrderValidationList = () => {
  const dispatch = useDispatch();
  const { coreSettings } = useSelector((state) => state.auth);
  const isMobile = useIsMobile();

  // State management
  const [validationData, setValidationData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(!isMobile);
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  // Active filter state
  const [activeFilters, setActiveFilters] = useState({
    status: '0', // Default to pending (status = 0)
    orderType: '',
    paymentType: '',
  });

  // Filter options
  const validationFilters = [
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: '', label: 'All Status' },
        { value: '0', label: 'Pending' },
        { value: '1', label: 'Validated' },
      ],
    },
    {
      key: 'orderType',
      label: 'Order Type',
      options: [
        { value: '', label: 'All Types' },
        { value: 'default', label: 'Default' },
        { value: 'delivery-partner', label: 'Delivery Partner' },
      ],
    },
    {
      key: 'paymentType',
      label: 'Payment Type',
      options: [
        { value: '', label: 'All Payment Types' },
        { value: 'paypal', label: 'PayPal' },
        { value: 'credit-card', label: 'Credit Card' },
        { value: 'cash', label: 'Cash' },
      ],
    },
  ];

  // Parse partner info
  const getPartnerInfo = (partnerInfoString) => {
    try {
      if (!partnerInfoString) return null;
      const parsed = JSON.parse(partnerInfoString);
      return parsed?.partner?.[0] || null;
    } catch {
      return null;
    }
  };

  // Handle status change
  const handleStatusChange = async (id, currentStatus) => {
    const newStatus = currentStatus ? 0 : 1;
    try {
      const result = await dispatch(
        updateOrderValidationStatus({ id, status: newStatus })
      ).unwrap();

      if (result?.status === 1) {
        toast.success('Status updated successfully');
        // Refresh the list
        fetchValidationList(currentPage, rowsPerPage, activeFilters);
      } else {
        toast.error(result?.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  // Define columns
  const columns = [
    {
      name: 'ID',
      selector: (row) => row.id,
      sortable: true,
      width: '80px',
    },
    {
      name: 'Order ID',
      cell: (row) => (
        <a
          href={`view-order-details/${encodeToBase64(row.payment_id)}`}
          className='text-primary-100 hover:text-primary-100 underline'
          target='_blank'
          rel='noopener noreferrer'
        >
          #{row.payment_id}
        </a>
      ),
      sortable: true,
      width: '100px',
    },
    {
      name: 'Order Type',
      cell: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.order_type === 'delivery-partner'
              ? 'bg-purple-100 text-purple-800'
              : 'bg-blue-100 text-blue-800'
          }`}
        >
          {row.order_type}
        </span>
      ),
      sortable: true,
      width: '140px',
    },
    {
      name: 'Partner Info',
      cell: (row) => {
        const partnerInfo = getPartnerInfo(row.partner_info);
        return partnerInfo ? (
          <div>
            <div className='font-medium text-sm'>{partnerInfo.name}</div>
            <div className='text-xs text-gray-500'>{partnerInfo.address}</div>
          </div>
        ) : (
          <span className='text-gray-400'>-</span>
        );
      },
    },
    {
      name: 'Payment Type',
      cell: (row) => <span className='capitalize'>{row.payment_type}</span>,
      sortable: true,
    },
    {
      name: 'Payment Status',
      cell: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.payment_status === 'pending'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-green-100 text-green-800'
          }`}
        >
          {row.payment_status}
        </span>
      ),
      sortable: true,
      width: '140px',
    },
    {
      name: 'Order Total',
      cell: (row) => <span className='font-medium'>{formatPrice(row.order_total)}</span>,
      sortable: true,
      width: '120px',
    },
    {
      name: 'Created At',
      cell: (row) => formatToUSDate(row.created_at),
      sortable: true,
    },
    {
      name: 'Status',
      cell: (row) => (
        <label className='relative inline-flex items-center cursor-pointer'>
          <input
            type='checkbox'
            checked={row.status}
            onChange={() => handleStatusChange(row.id, row.status)}
            className='sr-only peer'
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
        </label>
      ),
      width: '100px',
      center: true,
    }
  ];

  // Fetch validation list
  const fetchValidationList = async (page = 1, limit = 10, filters = {}) => {
    try {
      setIsLoading(page === 1);
      setIsFilterLoading(page !== 1);

      const params = {
        page,
        limit,
        status: filters.status !== undefined ? filters.status : activeFilters.status,
      };

      const result = await dispatch(getOrderValidationList(params)).unwrap();

      if (result?.status === 1 && result?.data) {
        // Filter by order type and payment type on frontend if needed
        let filteredData = result.data.order_validations || [];
        const orderTypeFilter = filters.orderType !== undefined ? filters.orderType : activeFilters.orderType;
        const paymentTypeFilter = filters.paymentType !== undefined ? filters.paymentType : activeFilters.paymentType;

        if (orderTypeFilter) {
          filteredData = filteredData.filter((item) => item.order_type === orderTypeFilter);
        }

        if (paymentTypeFilter) {
          filteredData = filteredData.filter((item) => item.payment_type === paymentTypeFilter);
        }

        // Apply search filter
        if (searchQuery) {
          const searchLower = searchQuery.toLowerCase();
          filteredData = filteredData.filter((item) => {
            return (
              item.order_id?.toString().includes(searchLower) ||
              item.payment_id?.toLowerCase().includes(searchLower) ||
              item.order_type?.toLowerCase().includes(searchLower) ||
              item.payment_type?.toLowerCase().includes(searchLower)
            );
          });
        }

        setValidationData(filteredData);
        setTotalRows(result.data.pagination?.total || 0);
        setCurrentPage(result.data.pagination?.page || 1);
      }
    } catch (error) {
      console.error('Failed to fetch order validation list:', error);
      toast.error('Failed to fetch order validation list');
    } finally {
      setIsLoading(false);
      setIsFilterLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchValidationList(currentPage, rowsPerPage, activeFilters);
  }, []);

  // Handle filter change
  const handleFilterChange = (filters) => {
    setCurrentPage(1);
    const filterParams = {
      status: filters.status !== undefined ? filters.status : '',
      orderType: filters.orderType || '',
      paymentType: filters.paymentType || '',
    };
    setActiveFilters(filterParams);
    fetchValidationList(1, rowsPerPage, filterParams);
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Debounced search effect
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setCurrentPage(1);
      fetchValidationList(1, rowsPerPage, activeFilters);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchValidationList(page, rowsPerPage, activeFilters);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage, page) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(page);
    fetchValidationList(page, newPerPage, activeFilters);
  };

  return (
    <section className='overflow-y-auto relative no-scrollbar'>
      <div className='container px-0'>
        <div className='p-4'>
          <div className='py-5 bg-neutral-100 px-5 mb-5 rounded-lg'>
            {/* Header Section */}
            <div className='flex flex-col md:flex-row md:justify-between md:items-center gap-4'>
              <h2 className='text-xl font-semibold'>Order Validation List</h2>

              {/* Search and Filter Controls */}
              <div className='flex gap-2 items-center'>
                {/* Search */}
                <div className='relative flex-1 md:w-60'>
                  <input
                    type='text'
                    placeholder='Search...'
                    value={searchQuery}
                    onChange={handleSearch}
                    className='bg-white border border-gray-300 rounded-lg px-4 py-3 text-sm pr-2 pl-10 outline-none focus:ring-2 focus:ring-red-700 focus:border-transparent hover:border-gray-400 transition-colors w-full'
                  />
                  <Search className='absolute top-1/2 left-4 -translate-y-1/2 h-4 w-4 text-primary-500' />
                </div>

                {/* Filter Button */}
                <button
                  className='admin-outline-btn px-3'
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Funnel className='admin-outline-btn-icon' />
                </button>
              </div>
            </div>
          </div>

          <ReusableDataTable
            columns={columns}
            data={validationData}
            filters={validationFilters}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            isLoading={isLoading}
            isFilterLoading={isFilterLoading}
            onFilterChange={handleFilterChange}
            visibleColumns={3}
            showCheckbox={false}
            showDateRangeFilter={false}
            paginationServer={true}
            paginationTotalRows={totalRows}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
          />
        </div>
      </div>
    </section>
  );
};

export default OrderValidationList;
